import type { ContactInsights } from '../types/contactInsights';

const DAY_MS = 24 * 60 * 60 * 1000;

// ISO date n days before today, so cadence states stay stable over time
const daysAgo = (days: number) => {
  const date = new Date(Date.now() - days * DAY_MS);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

// `count` purchases spaced `gap` days apart, the latest `lastDaysAgo` days ago
const purchases = (gap: number, lastDaysAgo: number, count = 4) => Array.from({
  length: count
}, (_, i) => daysAgo(lastDaysAgo + i * gap));

const insights = (values: ContactInsights): ContactInsights => values;

export const mockContacts = [{
  id: '1',
  name: 'Sarah Johnson',
  email: 'sarah.j@example.com',
  phone: '+1 (555) 123-4567',
  address: '123 Main St, San Francisco, CA',
  status: 'Active',
  subscriptionTitle: 'Premium Wine Club',
  paymentStatus: 'Current',
  atRisk: false,
  tags: ['VIP', 'Red Wine'],
  latestNote: 'Called about shipping delay',
  source: 'Shopify Checkout',
  memberSince: '2023-01-15',
  nextBilling: '2024-01-15',
  ltv: 1250.0,
  totalOrders: 12,
  lastOrder: daysAgo(14),
  aov: 104.16,
  insights: insights({
    callFlag: 'top-customer',
    hasActiveSubscription: true,
    watchedOrderDates: purchases(84, 33),
    lastContacted: {
      date: '2026-09-12',
      outcome: 'Voicemail'
    },
    spendTrendPct: 18,
    skips: 3,
    pauses: 1,
    clubOrders: 14,
    oneTimeOrders: 5,
    openTaskAssignees: ['Jude'],
    topProduct: '2021 Barossa Shiraz',
    productsPurchased: ['2021 Barossa Shiraz', '2023 Eden Valley Riesling', '2022 McLaren Vale Grenache']
  })
}, {
  id: '2',
  name: 'Michael Chen',
  email: 'michael.c@example.com',
  phone: '',
  address: '456 Oak Ave, Portland, OR',
  status: 'Paused',
  subscriptionTitle: 'Standard Box',
  paymentStatus: 'Current',
  atRisk: false,
  tags: ['White Wine'],
  latestNote: 'Paused for vacation',
  source: 'Manual',
  memberSince: '2023-03-10',
  nextBilling: '2024-02-10',
  ltv: 450.0,
  totalOrders: 5,
  lastOrder: daysAgo(102),
  aov: 90.0,
  insights: insights({
    callFlag: 'none',
    hasActiveSubscription: false,
    watchedOrderDates: purchases(60, 102),
    lastContacted: null,
    spendTrendPct: -32,
    skips: 1,
    pauses: 2,
    clubOrders: 4,
    oneTimeOrders: 2,
    openTaskAssignees: [],
    topProduct: '2023 Eden Valley Riesling',
    productsPurchased: ['2023 Eden Valley Riesling', '2023 Adelaide Hills Chardonnay']
  })
}, {
  id: '3',
  name: 'Emma Wilson',
  email: 'emma.w@example.com',
  phone: '+1 (555) 456-7890',
  address: '789 Pine Ln, Seattle, WA',
  status: 'Active',
  subscriptionTitle: 'Premium Wine Club',
  paymentStatus: 'Failed',
  atRisk: true,
  tags: ['Retry'],
  latestNote: 'Payment failed twice',
  source: 'Shopify Checkout',
  memberSince: '2022-11-05',
  nextBilling: '2024-01-05',
  ltv: 890.0,
  totalOrders: 8,
  lastOrder: daysAgo(30),
  aov: 111.25,
  insights: insights({
    callFlag: 'dont-call',
    hasActiveSubscription: true,
    watchedOrderDates: purchases(84, 126),
    lastContacted: {
      date: '2026-10-02',
      outcome: 'No answer'
    },
    spendTrendPct: -12,
    skips: 2,
    pauses: 0,
    clubOrders: 7,
    oneTimeOrders: 1,
    openTaskAssignees: ['Eranna'],
    topProduct: '2022 McLaren Vale Grenache',
    productsPurchased: ['2022 McLaren Vale Grenache', '2021 Barossa Shiraz']
  })
}, {
  id: '4',
  name: 'James Brown',
  email: 'james.b@example.com',
  phone: '+1 (555) 222-3333',
  address: '321 Elm St, Austin, TX',
  status: 'Cancelled',
  subscriptionTitle: 'Starter Pack',
  paymentStatus: 'Current',
  atRisk: false,
  tags: ['Churned'],
  latestNote: 'Cancelled via email',
  source: 'POS',
  memberSince: '2023-06-20',
  nextBilling: '-',
  ltv: 150.0,
  totalOrders: 3,
  lastOrder: daysAgo(180),
  aov: 50.0,
  insights: insights({
    callFlag: 'not-worth-chasing',
    hasActiveSubscription: false,
    watchedOrderDates: purchases(45, 180, 3),
    lastContacted: {
      date: '2026-08-20',
      outcome: 'Not interested'
    },
    spendTrendPct: -64,
    skips: 0,
    pauses: 0,
    clubOrders: 2,
    oneTimeOrders: 1,
    openTaskAssignees: [],
    topProduct: '2023 Adelaide Hills Chardonnay',
    productsPurchased: ['2023 Adelaide Hills Chardonnay']
  })
}, {
  id: '5',
  name: 'Olivia Davis',
  email: 'olivia.d@example.com',
  phone: '+1 (555) 777-8888',
  address: '654 Maple Dr, Denver, CO',
  status: 'Active',
  subscriptionTitle: 'Premium Wine Club',
  paymentStatus: 'Current',
  atRisk: false,
  tags: ['VIP', 'Referral'],
  latestNote: 'Loves the Pinot Noir',
  source: 'Referral',
  memberSince: '2022-08-15',
  nextBilling: '2024-01-15',
  ltv: 1800.0,
  totalOrders: 15,
  lastOrder: daysAgo(20),
  aov: 120.0,
  insights: insights({
    callFlag: 'top-customer',
    hasActiveSubscription: true,
    watchedOrderDates: purchases(70, 62),
    lastContacted: {
      date: '2026-07-02',
      outcome: 'Ordered'
    },
    spendTrendPct: 24,
    skips: 0,
    pauses: 0,
    clubOrders: 12,
    oneTimeOrders: 3,
    openTaskAssignees: [],
    topProduct: '2022 Yarra Valley Pinot Noir',
    productsPurchased: ['2022 Yarra Valley Pinot Noir', '2020 Coonawarra Cabernet Sauvignon']
  })
}, {
  id: '6',
  name: 'Daniel Nguyen',
  email: 'daniel.n@example.com',
  phone: '+1 (555) 314-2718',
  address: '88 Harbor Blvd, San Diego, CA',
  status: 'Active',
  subscriptionTitle: 'Standard Box',
  paymentStatus: 'Failed',
  atRisk: true,
  tags: ['Red Wine'],
  latestNote: 'Asked about magnums',
  source: 'Shopify Checkout',
  memberSince: '2024-02-01',
  nextBilling: '2024-02-01',
  ltv: 640.0,
  totalOrders: 11,
  lastOrder: daysAgo(12),
  aov: 58.18,
  insights: insights({
    callFlag: 'none',
    hasActiveSubscription: true,
    watchedOrderDates: purchases(56, 20),
    lastContacted: null,
    spendTrendPct: 5,
    skips: 1,
    pauses: 0,
    clubOrders: 9,
    oneTimeOrders: 2,
    openTaskAssignees: ['Jude', 'Chris'],
    topProduct: '2020 Coonawarra Cabernet Sauvignon',
    productsPurchased: ['2020 Coonawarra Cabernet Sauvignon', '2021 Barossa Shiraz']
  })
}, {
  id: '7',
  name: 'Grace Thompson',
  email: 'grace.t@example.com',
  phone: '+1 (555) 600-1212',
  address: '17 Vine St, Napa, CA',
  status: 'Active',
  subscriptionTitle: 'Premium Wine Club',
  paymentStatus: 'Current',
  atRisk: false,
  tags: ['VIP'],
  latestNote: 'Hosting a dinner in November',
  source: 'Referral',
  memberSince: '2021-05-12',
  nextBilling: '2024-01-12',
  ltv: 3120.0,
  totalOrders: 24,
  lastOrder: daysAgo(9),
  aov: 130.0,
  insights: insights({
    callFlag: 'top-customer',
    hasActiveSubscription: true,
    watchedOrderDates: purchases(42, 30, 6),
    lastContacted: {
      date: '2026-10-08',
      outcome: 'Answered'
    },
    spendTrendPct: 9,
    skips: 0,
    pauses: 0,
    clubOrders: 18,
    oneTimeOrders: 6,
    openTaskAssignees: ['Sandeep'],
    topProduct: '2019 Reserve Shiraz',
    productsPurchased: ['2019 Reserve Shiraz', '2021 Barossa Shiraz', '2022 Yarra Valley Pinot Noir']
  })
}, {
  id: '8',
  name: "Liam O'Connor",
  email: 'liam.o@example.com',
  phone: '+1 (555) 441-9030',
  address: '402 Lake Rd, Chicago, IL',
  status: 'Active',
  subscriptionTitle: 'Reds Club',
  paymentStatus: 'Current',
  atRisk: false,
  tags: ['Red Wine'],
  latestNote: 'Prefers bold reds',
  source: 'Shopify Checkout',
  memberSince: '2022-09-03',
  nextBilling: '2024-01-03',
  ltv: 980.0,
  totalOrders: 12,
  lastOrder: daysAgo(25),
  aov: 81.67,
  insights: insights({
    callFlag: 'none',
    hasActiveSubscription: true,
    watchedOrderDates: purchases(63, 105),
    lastContacted: {
      date: '2026-07-15',
      outcome: 'Voicemail'
    },
    spendTrendPct: -21,
    skips: 4,
    pauses: 0,
    clubOrders: 10,
    oneTimeOrders: 2,
    openTaskAssignees: [],
    topProduct: '2021 Barossa Shiraz',
    productsPurchased: ['2021 Barossa Shiraz', '2022 McLaren Vale Grenache']
  })
}, {
  id: '9',
  name: 'Chloe Martin',
  email: 'chloe.m@example.com',
  phone: '+1 (555) 818-4466',
  address: '9 Ridge Ct, Boulder, CO',
  status: 'Paused',
  subscriptionTitle: 'Whites Club',
  paymentStatus: 'Current',
  atRisk: true,
  tags: ['White Wine'],
  latestNote: 'Pause ends in January',
  source: 'Manual',
  memberSince: '2023-04-22',
  nextBilling: '2024-03-22',
  ltv: 410.0,
  totalOrders: 5,
  lastOrder: daysAgo(150),
  aov: 82.0,
  insights: insights({
    callFlag: 'none',
    hasActiveSubscription: false,
    watchedOrderDates: purchases(90, 150),
    lastContacted: {
      date: '2026-09-01',
      outcome: 'No answer'
    },
    spendTrendPct: -18,
    skips: 1,
    pauses: 1,
    clubOrders: 5,
    oneTimeOrders: 3,
    openTaskAssignees: ['Eranna'],
    topProduct: '2023 Adelaide Hills Chardonnay',
    productsPurchased: ['2023 Adelaide Hills Chardonnay', '2025 Fiano']
  })
}, {
  id: '10',
  name: 'Noah Patel',
  email: 'noah.p@example.com',
  phone: '+1 (555) 272-5150',
  address: '230 Cedar Ave, Phoenix, AZ',
  status: 'Active',
  subscriptionTitle: 'Standard Box',
  paymentStatus: 'Current',
  atRisk: false,
  tags: ['Referral'],
  latestNote: 'Joined from a friend referral',
  source: 'Referral',
  memberSince: '2023-07-09',
  nextBilling: '2024-01-09',
  ltv: 720.0,
  totalOrders: 8,
  lastOrder: daysAgo(28),
  aov: 90.0,
  insights: insights({
    callFlag: 'none',
    hasActiveSubscription: true,
    watchedOrderDates: purchases(120, 40, 3),
    lastContacted: {
      date: '2026-09-22',
      outcome: 'Answered'
    },
    spendTrendPct: 2,
    skips: 0,
    pauses: 0,
    clubOrders: 6,
    oneTimeOrders: 2,
    openTaskAssignees: [],
    topProduct: '2025 Rosé Grenache',
    productsPurchased: ['2025 Rosé Grenache', '2025 Fiano']
  })
}];

export type MockContact = (typeof mockContacts)[number];
